import { ArrowUpDown, CheckCircle2, Eye, Loader2, XCircle } from 'lucide-react';
import { Pagination } from '@/components/shared/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCNPJ, formatCurrency, formatDate } from '@/lib/utils/formatters';
import type { NfseDocumento } from '@/services/ServicosNfse';
import { getChaveAcessoDoc, hasDateValue, parseCurrencyValue } from '../utils';

/* Manifestar saiu do menu da linha e foi para o detalhe (SPEC-011, PR 4): lá a rejeição pede o motivo (T8) e a
   resposta do serviço aparece. O olho abre o detalhe. */
interface NfseTableProps {
  documentos: NfseDocumento[];
  totalItens: number;
  isLoading: boolean;
  isFetching: boolean;
  tipo: 'tomados' | 'prestados';
  paginaAtual: number;
  itensPorPagina: number;
  sortField: SortField;
  sortDirection: SortDirection;
  onPaginaChange: (pagina: number) => void;
  onItensPorPaginaChange: (itensPorPagina: number) => void;
  onSelectDocumento: (documento: NfseDocumento) => void;
  onSortChange: (field: SortField) => void;
}

type SortField = 'numero' | 'prestador' | 'data' | 'servico' | 'status' | 'valor' | 'ibs' | 'cbs';
type SortDirection = 'asc' | 'desc';

function getStatusMeta(status: string) {
  const normalized = String(status || '')
    .trim()
    .toUpperCase();

  const statusMap: Record<
    string,
    { label: string; variant: 'default' | 'destructive' | 'secondary' | 'outline' }
  > = {
    ACEITA: { label: 'Aceita', variant: 'default' },
    REJEITADA: { label: 'Rejeitada', variant: 'destructive' },
    PENDENTE: { label: 'Pendente', variant: 'secondary' },
    SEM_MANIFESTACAO: { label: 'Sem Manifestação', variant: 'outline' },
    AUTORIZADA: { label: 'Autorizada', variant: 'default' },
    CANCELADA: { label: 'Cancelada', variant: 'destructive' },
    '0': { label: 'Pendente', variant: 'secondary' },
    '4': { label: 'Autorizada', variant: 'default' },
  };

  return statusMap[normalized] || { label: status || '-', variant: 'outline' as const };
}

function getAceiteMeta(documento: NfseDocumento) {
  if (hasDateValue(documento.DataCancelamento)) {
    return { label: 'Cancelada', variant: 'destructive' as const };
  }

  if (hasDateValue(documento.DataRejeicao)) {
    return { label: 'Rejeitada', variant: 'destructive' as const };
  }

  if (hasDateValue(documento.DataConfirmacao)) {
    return { label: 'Confirmada', variant: 'default' as const };
  }

  return getStatusMeta(documento.Status);
}

function StatusBadge({
  documento,
  tipo,
}: {
  documento: NfseDocumento;
  tipo: 'tomados' | 'prestados';
}) {
  // Prestadas não têm aceite do tomador, mas a cancelada (por DataCancelamento, que a lista
  // agora preenche a partir do evento 101101 — SPEC-011 decisão 13) não pode seguir "Autorizada".
  const meta =
    tipo === 'tomados'
      ? getAceiteMeta(documento)
      : hasDateValue(documento.DataCancelamento)
        ? { label: 'Cancelada', variant: 'destructive' as const }
        : getStatusMeta(documento.Status);
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function CreditoBadge({ documento }: { documento: NfseDocumento }) {
  const temDireito =
    documento.TipoOperacao === '0' &&
    (parseCurrencyValue(documento.vIBS) > 0 || parseCurrencyValue(documento.vCBS) > 0);

  return temDireito ? (
    <Badge className="bg-green-500">
      <CheckCircle2 className="mr-1 h-3 w-3" />
      Com Direito
    </Badge>
  ) : (
    <Badge variant="destructive">
      <XCircle className="mr-1 h-3 w-3" />
      Sem Direito
    </Badge>
  );
}

function getTributoTextColor(documento: NfseDocumento) {
  const temValorTributo =
    parseCurrencyValue(documento.vIBS) > 0 || parseCurrencyValue(documento.vCBS) > 0;

  return documento.TipoOperacao === '0' && temValorTributo ? 'text-green-600' : 'text-red-600';
}

export function NfseTable({
  documentos,
  totalItens,
  isLoading,
  isFetching,
  tipo,
  paginaAtual,
  itensPorPagina,
  sortField,
  sortDirection,
  onPaginaChange,
  onItensPorPaginaChange,
  onSelectDocumento,
  onSortChange,
}: NfseTableProps) {
  const colunaPessoa = tipo === 'tomados' ? 'Prestador' : 'Tomador';
  const colunaStatus = tipo === 'tomados' ? 'Aceite' : 'Situação';
  const colSpan = tipo === 'tomados' ? 10 : 9;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));
  /* O fim sai das LINHAS desenhadas, não do tamanho da página: o servidor pode devolver
     menos linhas do que cabem, e `paginaAtual * itensPorPagina` prometeria registros que
     não estão na tela. O total continua sendo o do servidor, que é outra conta. */
  const inicio = documentos.length === 0 ? 0 : (paginaAtual - 1) * itensPorPagina + 1;
  const fim = documentos.length === 0 ? 0 : inicio + documentos.length - 1;
  const isAtualizandoTabela = isLoading || isFetching;

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field)
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/70" />;
    return (
      <span className="text-[11px] font-semibold leading-none text-foreground">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        {isAtualizandoTabela && (
          <div className="absolute inset-0 z-10 flex min-h-[220px] items-center justify-center bg-background/95 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Atualizando NFS-e...
          </div>
        )}
        <div className={isAtualizandoTabela ? 'opacity-0' : ''}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5"
                    onClick={() => onSortChange('numero')}
                  >
                    Número
                    {getSortIndicator('numero')}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5"
                    onClick={() => onSortChange('prestador')}
                  >
                    {colunaPessoa}
                    {getSortIndicator('prestador')}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5"
                    onClick={() => onSortChange('servico')}
                  >
                    Serviço
                    {getSortIndicator('servico')}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5"
                    onClick={() => onSortChange('data')}
                  >
                    Data
                    {getSortIndicator('data')}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5"
                    onClick={() => onSortChange('status')}
                  >
                    {colunaStatus}
                    {getSortIndicator('status')}
                  </button>
                </TableHead>
                {tipo === 'tomados' && <TableHead>Crédito</TableHead>}
                <TableHead className="text-right">
                  <button
                    type="button"
                    className="inline-flex items-center justify-end gap-1.5"
                    onClick={() => onSortChange('valor')}
                  >
                    Valor
                    {getSortIndicator('valor')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    className="inline-flex items-center justify-end gap-1.5"
                    onClick={() => onSortChange('ibs')}
                  >
                    IBS
                    {getSortIndicator('ibs')}
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button
                    type="button"
                    className="inline-flex items-center justify-end gap-1.5"
                    onClick={() => onSortChange('cbs')}
                  >
                    CBS
                    {getSortIndicator('cbs')}
                  </button>
                </TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documentos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="py-12 text-center text-muted-foreground">
                    Nenhuma NFS-e encontrada para os filtros informados.
                  </TableCell>
                </TableRow>
              ) : (
                documentos.map(documento => {
                  const textoValorTributo = getTributoTextColor(documento);
                  const hasChaveAcesso = !!getChaveAcessoDoc(documento);

                  return (
                    <TableRow
                      key={`${tipo}-${documento.CNPJ_Contribuinte}-${documento.Numero}-${documento.CaminhoArquivo}`}
                    >
                      <TableCell className="font-mono text-sm">{documento.Numero}</TableCell>
                      <TableCell className="max-w-[320px]">
                        <div>
                          <p className="truncate text-sm font-medium" title={documento.RazaoSocial}>
                            {documento.RazaoSocial}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCNPJ(documento.CNPJ)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[260px]">
                        <div className="truncate text-sm" title={documento.servicoDescricao}>
                          {documento.servicoDescricao || '-'}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Código: {documento.servicoCodigo || '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        {formatDate(documento.DataEmissao || documento.dataHoraGravacao)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge documento={documento} tipo={tipo} />
                      </TableCell>
                      {tipo === 'tomados' && (
                        <TableCell>
                          <CreditoBadge documento={documento} />
                        </TableCell>
                      )}
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(parseCurrencyValue(documento.vNF))}
                      </TableCell>
                      <TableCell className={`text-right ${textoValorTributo}`}>
                        {formatCurrency(parseCurrencyValue(documento.vIBS))}
                      </TableCell>
                      <TableCell className={`text-right ${textoValorTributo}`}>
                        {formatCurrency(parseCurrencyValue(documento.vCBS))}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => onSelectDocumento(documento)}
                            disabled={!hasChaveAcesso}
                            aria-label="Visualizar detalhes da NFS-e"
                            title="Visualizar detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {totalItens > 0 && (
        <div className="flex flex-col gap-3 py-2 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm text-muted-foreground">
            Exibindo {inicio} a {fim} de {totalItens} registros
          </p>

          <Pagination
            currentPage={paginaAtual}
            totalPages={totalPaginas}
            onPageChange={onPaginaChange}
            itensPorPagina={itensPorPagina}
            onItensPorPaginaChange={onItensPorPaginaChange}
          />
        </div>
      )}
    </div>
  );
}
